import type { Permission, SessionDto } from '@cartly/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError, setCsrfToken } from '../api/client';

type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'mfa_pending'; session: SessionDto }
  | { status: 'active'; session: SessionDto };

interface SessionApi {
  state: SessionState;
  /** Re-reads the session from the server (after login, switching tenant, etc.). */
  refresh: () => Promise<SessionState>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const SessionContext = createContext<SessionApi | null>(null);

async function fetchSession(): Promise<SessionState> {
  try {
    const session = await api.get<SessionDto>('/auth/session');
    setCsrfToken(session.csrfToken);
    return session.state === 'mfa_pending'
      ? { status: 'mfa_pending', session }
      : { status: 'active', session };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      setCsrfToken(null);
      return { status: 'anonymous' };
    }
    throw err;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  const refresh = useCallback(async (): Promise<SessionState> => {
    const next = await fetchSession();
    setState(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setCsrfToken(null);
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    fetchSession().then(setState, () => setState({ status: 'anonymous' }));
  }, []);

  const value = useMemo<SessionApi>(() => {
    const permissions = new Set(
      state.status === 'active' ? (state.session.activeTenant?.permissions ?? []) : [],
    );
    return { state, refresh, logout, can: (p) => permissions.has(p) };
  }, [state, refresh, logout]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** For components rendered only inside the authenticated app shell. */
export function useActiveSession(): SessionDto {
  const { state } = useSession();
  if (state.status !== 'active') throw new Error('No active session');
  return state.session;
}
